export default function CFIsDebrief() {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-6">
            <i className="ri-video-line text-teal-600 text-lg"></i>
            <span className="text-sm font-medium text-teal-700">Post-Flight Analysis</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Interactive Debrief Tools
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transform post-flight debriefs with visual replay, AI-powered insights, and annotation tools that help students understand exactly what happened and why.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xl">
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="text-sm text-gray-400 font-mono">Flight Replay Theater</div>
                <div className="w-20"></div>
              </div>
              
              <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 aspect-video">
                <img 
                  src="https://public.readdy.ai/ai/img_res/f21fcfff82012667fba129be941a8faf.jpg"
                  alt="Flight replay visualization"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                  <div className="bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
                    <div className="text-xs text-gray-400 mb-1">Student</div>
                    <div className="text-sm font-bold text-white">Sarah Mitchell</div>
                  </div>
                  <div className="bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
                    <div className="text-xs text-gray-400 mb-1">Flight Date</div>
                    <div className="text-sm font-bold text-white">Jan 15, 2025</div>
                  </div>
                </div>

                <div className="absolute bottom-4 left-4 right-4">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20">
                      <div className="text-xs text-gray-400">Altitude</div>
                      <div className="text-lg font-bold text-white">1,850 ft</div>
                    </div>
                    <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20">
                      <div className="text-xs text-gray-400">Speed</div>
                      <div className="text-lg font-bold text-white">78 kts</div>
                    </div>
                    <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20">
                      <div className="text-xs text-gray-400">Heading</div>
                      <div className="text-lg font-bold text-white">270°</div>
                    </div>
                    <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20">
                      <div className="text-xs text-gray-400">Time</div>
                      <div className="text-lg font-bold text-white">12:34</div>
                    </div>
                  </div>

                  <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                    <div className="flex items-center gap-3 mb-2">
                      <button className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center hover:bg-teal-700 transition-colors cursor-pointer">
                        <i className="ri-play-fill text-white text-xl"></i>
                      </button>
                      <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="w-2/3 h-full bg-teal-500 rounded-full"></div>
                      </div>
                      <span className="text-sm text-white font-mono">08:45 / 13:20</span>
                    </div>
                  </div>
                </div>

                <div className="absolute top-1/2 right-4 -translate-y-1/2 space-y-2">
                  <button className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-colors cursor-pointer shadow-lg">
                    <i className="ri-flag-fill text-white"></i>
                  </button>
                  <button className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer shadow-lg">
                    <i className="ri-error-warning-fill text-white"></i>
                  </button>
                  <button className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer shadow-lg">
                    <i className="ri-chat-3-fill text-white"></i>
                  </button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-information-line text-teal-600"></i>
                  <span>Click anywhere on the timeline to add annotations and coaching notes</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Visual Replay with Annotations
              </h3>
              <p className="text-gray-600 mb-6">
                Review every moment of the flight with synchronized data overlays. Add markers, notes, and coaching points at specific timestamps to create a comprehensive debrief record.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Timestamp Markers</h4>
                    <p className="text-sm text-gray-600">Flag key moments for discussion during the debrief</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-edit-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Coaching Notes</h4>
                    <p className="text-sm text-gray-600">Add detailed feedback and improvement suggestions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-share-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Share with Students</h4>
                    <p className="text-sm text-gray-600">Students can review annotated flights on their own time</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-white text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">AI-Powered Insights</h4>
                  <p className="text-gray-600 text-sm mb-3">
                    WingmanFix automatically identifies teaching moments and suggests coaching points based on the flight data.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600"></i>
                      <span>Pattern deviations and corrections</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600"></i>
                      <span>Radio communication timing</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600"></i>
                      <span>Stabilized approach criteria</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Sample Debrief Session</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-flag-fill text-white"></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-bold text-gray-900">08:23 — Downwind Entry</span>
                  <span className="text-xs text-gray-600">Instructor Note</span>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Great radio call and entry angle. Notice how you maintained 1,800 ft throughout the entry — that's exactly what we want to see.
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium whitespace-nowrap">Well Done</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">Radio Communication</span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-fill text-white"></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-bold text-gray-900">10:45 — Base to Final Turn</span>
                  <span className="text-xs text-gray-600">Coaching Point</span>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  You started the turn a bit late, which led to overshooting the centerline. Next time, start your turn when the runway is at a 45° angle behind you. Let's practice this on the next pattern.
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium whitespace-nowrap">Needs Work</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">Pattern Work</span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-white"></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-bold text-gray-900">12:18 — Final Approach</span>
                  <span className="text-xs text-gray-600">AI Insight</span>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  WingmanFix detected a stabilized approach with consistent airspeed (65 kts ±3) and descent rate (500 fpm). This is excellent progress compared to previous flights.
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium whitespace-nowrap">AI Analysis</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">Landing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}